import { createKineticScroll } from "./kineticScroll";
import { createHoverFeedback } from "./hoverFeedback";
import { createPointerGestures } from "./pointerGestures";
import { handleWheel, handleKeyDown } from "./wheelAndKeys";

const MOUSE = "mouse";

/**
 * All gestures of the chart (E8, docs/engine-ref-images/README.md): pan with
 * inertia, wheel and pinch zoom, axis drags and double taps,
 * long-press crosshair on touch, drawings and keys of the focused chart. Listens on
 * the chart container only, so clicks on the React toolbar above it (a
 * sibling element) never reach the chart.
 *
 * @param {HTMLElement} container
 * @param {object} params
 * @param {object} params.timeScale
 * @param {object} params.priceScale
 * @param {object} params.overlay - crosshair overlay: layout of the last frame, crosshair
 * @param {object} params.positions - positionsState: close buttons of position cards
 * @param {object} params.drawings - createDrawings: hover, presses and gestures of drawings
 * @param {object} params.paneStack - scrolls the oscillator panes that do not fit
 * @param {(step: Function) => () => void} params.animate - scheduler.animate, for the inertia
 * @param {() => void} params.invalidate - schedules a frame
 * @returns {{ cancelGesture: () => void, destroy: () => void }}
 */
export const createInteraction = (
  container,
  { timeScale, priceScale, overlay, positions, drawings, paneStack, animate, invalidate },
) => {
  const kinetic = createKineticScroll({
    animate,
    scrollBy: (pixels) => timeScale.scrollBy(pixels),
  });
  const view = { timeScale, priceScale, overlay, positions, drawings, paneStack, kinetic, invalidate };
  const hover = createHoverFeedback(container, overlay, positions, drawings);

  const localPoint = (event) => {
    const rect = container.getBoundingClientRect();

    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const pointerGestures = createPointerGestures({ container, view, hover, localPoint });

  const handleWheelEvent = (event) => {
    if (!overlay.layout) return;

    // A plain wheel is left to the page; only what the chart took stops its scroll
    if (!handleWheel(event, localPoint(event), view)) return;

    event.preventDefault();
    kinetic.stop();
  };

  const handleKey = (event) => {
    if (!overlay.layout || !handleKeyDown(event, view)) return;

    event.preventDefault();
    kinetic.stop();
  };

  const handlePointerLeave = (event) => {
    if (event.pointerType === MOUSE && !pointerGestures.isPressed) hover.leave();
  };

  // A long press on touch must not open the context menu
  const handleContextMenu = (event) => {
    if (pointerGestures.isPressed) event.preventDefault();
  };

  // The browser asks about each finger move until it has started to scroll
  const handleTouchMove = (event) => {
    if (event.cancelable && pointerGestures.holdsPageScroll) event.preventDefault();
  };

  const listeners = [
    ["pointerdown", pointerGestures.handlePointerDown],
    ["pointermove", pointerGestures.handlePointerMove],
    ["pointerup", (event) => pointerGestures.finishPointer(event, false)],
    ["pointercancel", (event) => pointerGestures.finishPointer(event, true)],
    ["pointerleave", handlePointerLeave],
    ["contextmenu", handleContextMenu],
    // Not passive: the page must not scroll under a wheel the chart took (handleWheel)
    ["wheel", handleWheelEvent, { passive: false }],
    // Not passive for the same reason; pointer events come before it
    ["touchmove", handleTouchMove, { passive: false }],
    ["keydown", handleKey],
  ];

  listeners.forEach(([type, listener, options]) =>
    container.addEventListener(type, listener, options),
  );
  // Focusable, so the arrow keys reach the chart after a click on it
  container.tabIndex = 0;

  return {
    /** Ends what the pointer is doing now, as if it was cancelled. */
    cancelGesture: pointerGestures.cancel,

    destroy: () => {
      kinetic.stop();
      pointerGestures.cancel();
      listeners.forEach(([type, listener, options]) =>
        container.removeEventListener(type, listener, options),
      );
      container.removeAttribute("tabindex");
      hover.destroy();
    },
  };
};
